import { Component, OnInit, ViewChild } from '@angular/core';
import {
  ModalBaseConfig,
  ModalBaseComponent,
} from '@app/core/components/modal-base/modal-base.component';
import { Modal } from '@app/core/interfaces/modal.interface';
import {
  Company,
  AdditionalField,
  CreateAdditionalFieldGQL,
  DeleteAdditionalFieldGQL,
  UpdateAdditionalFieldGQL,
} from '@app/generated/graphql';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { ToastService } from '@app/core/services/toast/toast.service';

export interface AdditionalFieldsModalConfig extends ModalBaseConfig {
  company: Company;
}

type FormMode = 'Add' | 'Edit';

@Component({
  selector: 'ag-addtional-fields-modal',
  templateUrl: './addtional-fields-modal.component.html',
  styleUrls: ['./addtional-fields-modal.component.scss'],
  standalone: false,
})
export class AddtionalFieldsModalComponent implements OnInit, Modal {
  @ViewChild(ModalBaseComponent, { static: true })
  modalBase: ModalBaseComponent;
  company: Company;
  showTable = true;
  formMode: FormMode = 'Add';
  deleteAdditionalFieldId: number;
  formSubmitted = false;
  additionalFieldForm: UntypedFormGroup;
  deletingAdditionalField = false;
  addtionalFieldEditName = '';

  constructor(
    private toastService: ToastService,
    private createAdditionalFieldGql: CreateAdditionalFieldGQL,
    private deleteAdditionalFieldGql: DeleteAdditionalFieldGQL,
    private updateAddtionalFieldGql: UpdateAdditionalFieldGQL,
  ) {}

  get formControls() {
    return this.additionalFieldForm.controls;
  }

  existingAdditionalFieldNameValidator(company: Company): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const invalid =
          company.additionalFields
            .map((a) => a.name.toLowerCase().replace(' ', ''))
            .indexOf(control.value.toLowerCase().replace(/ /g, '')) > -1 &&
          control.value.toLowerCase().replace(/ /g, '') !==
            this.addtionalFieldEditName.toLowerCase().replace(/ /g, '');
        return invalid
          ? { existingAdditionalField: { value: control.value } }
          : null;
      }
      return null;
    };
  }

  existingColumnNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const invalid =
          control.value.toLowerCase() === 'commodity' ||
          control.value.toLowerCase() === 'variety' ||
          control.value.toLowerCase().replace(/ /g, '') === 'targetregion' ||
          control.value.toLowerCase().replace(/ /g, '') === 'targetcountry' ||
          control.value.toLowerCase().replace(/ /g, '') === 'targetmarket' ||
          control.value.toLowerCase() === 'incoterm' ||
          control.value.toLowerCase().replace(/ /g, '') === 'packtype' ||
          control.value.toLowerCase() === 'counts' ||
          control.value.toLowerCase().replace(/ /g, '') === 'stacksize' ||
          control.value.toLowerCase() === 'quantity' ||
          control.value.toLowerCase() === 'price' ||
          control.value.toLowerCase() === 'grade';
        return invalid ? { existingColumn: { value: control.value } } : null;
      }
      return null;
    };
  }

  ngOnInit() {
    this.additionalFieldForm = new UntypedFormGroup({
      name: new UntypedFormControl(null, [
        Validators.required,
        this.existingColumnNameValidator(),
        this.existingAdditionalFieldNameValidator(this.company),
      ]),
      description: new UntypedFormControl(null, [Validators.required]),
      type: new UntypedFormControl(null, [Validators.required]),
      parameters: new UntypedFormControl(null, [Validators.required]),
      packingInstruction: new UntypedFormControl(false),
      private: new UntypedFormControl(false),
    });

    this.additionalFieldForm.get('type').valueChanges.subscribe((type) => {
      if (['dropdown', 'numeric'].includes(type)) {
        this.additionalFieldForm.addControl(
          'parameters',
          new UntypedFormControl(null, [Validators.required]),
        );
      } else if (type === 'text') {
        this.additionalFieldForm.removeControl('parameters');
      }
    });
  }

  configure(config: AdditionalFieldsModalConfig) {
    this.company = config.company;
    this.modalBase.configure(config);
  }

  showForm(formMode: FormMode, additionalField?: AdditionalField) {
    this.additionalFieldForm.get('packingInstruction').setValue(false); // reset bool (from 'nil') in case modal kept open between additions
    this.additionalFieldForm.get('private').setValue(false);
    this.deleteAdditionalFieldId = null;
    this.formMode = formMode;
    this.showTable = false;
    this.modalBase.setTitle(`${this.formMode} Additional Field`);
    this.modalBase.setCancelBtnText('Back');
    this.modalBase.setCanConfirm(true);
    this.modalBase.setOnCancel(() => this.hideForm());
    if (this.formMode === 'Edit') {
      this.addtionalFieldEditName = additionalField.name;
      this.additionalFieldForm.get('name').setValue(additionalField.name);
      this.additionalFieldForm
        .get('description')
        .setValue(additionalField.description);
      this.additionalFieldForm.get('type').setValue(additionalField.fieldType);
      this.additionalFieldForm
        .get('packingInstruction')
        .setValue(additionalField.isPackingInstruction);
      this.additionalFieldForm.get('private').setValue(additionalField.private);
      if (['dropdown', 'numeric'].includes(additionalField.fieldType)) {
        this.additionalFieldForm
          .get('parameters')
          .setValue(additionalField.parameters);
      }
      this.modalBase.setOnConfirm(() =>
        this.updateAdditionalField(additionalField),
      );
    } else {
      this.modalBase.setOnConfirm(() => this.addAdditionalField());
    }
  }

  hideForm() {
    this.modalBase.setCancelBtnText('Close');
    this.modalBase.setCanConfirm(false);
    this.modalBase.setOnCancel(() => this.hide());
    this.modalBase.setTitle('Additional Fields');
    this.showTable = true;
    this.formSubmitted = false;
    this.additionalFieldForm.reset();
  }

  showDeleteAdditionalField(additionalFieldId: number) {
    this.deleteAdditionalFieldId = additionalFieldId;
  }

  addAdditionalField() {
    this.formSubmitted = true;
    if (this.additionalFieldForm.invalid) {
      return;
    }

    this.modalBase.setLoading(true);
    this.createAdditionalFieldGql
      .mutate({
        attributes: {
          companyid: this.company.id,
          name: this.additionalFieldForm.get('name').value,
          description: this.additionalFieldForm.get('description').value,
          fieldtype: this.additionalFieldForm.get('type').value,
          ispackinginstruction:
            this.additionalFieldForm.get('packingInstruction').value,
          private: this.additionalFieldForm.get('private').value,
          parameters: ['dropdown', 'numeric'].includes(
            this.additionalFieldForm.get('type').value,
          )
            ? this.additionalFieldForm.get('parameters').value
            : null,
        },
      })
      .subscribe(
        (res) => {
          this.company.additionalFields.push(
            res.data.createAdditionalField.additionalField,
          );
          this.toastService.showToast('Additional Field created');
          this.hideForm();
          this.modalBase.setLoading(false);
        },
        (err) => {
          this.modalBase.setLoading(false);
          this.toastService.showErrorToast('Failed to create Additional Field');
          throw err;
        },
      );
  }

  updateAdditionalField(additionalField: AdditionalField) {
    this.formSubmitted = true;
    if (this.additionalFieldForm.invalid) {
      return;
    }

    this.modalBase.setLoading(true);
    this.updateAddtionalFieldGql
      .mutate({
        id: additionalField.id,
        attributes: {
          name: this.additionalFieldForm.get('name').value,
          description: this.additionalFieldForm.get('description').value,
          fieldtype: this.additionalFieldForm.get('type').value,
          ispackinginstruction:
            this.additionalFieldForm.get('packingInstruction').value,
          private: this.additionalFieldForm.get('private').value,
          parameters: ['dropdown', 'numeric'].includes(
            this.additionalFieldForm.get('type').value,
          )
            ? this.additionalFieldForm.get('parameters').value
            : null,
        },
      })
      .subscribe(
        (res) => {
          this.company.additionalFields[
            this.company.additionalFields.findIndex(
              (af) => af.id === additionalField.id,
            )
          ] = res.data.updateAdditionalField.additionalField;
          this.toastService.showToast('Additional Field updated');
          this.hideForm();
          this.modalBase.setLoading(false);
        },
        (err) => {
          this.modalBase.setLoading(false);
          this.toastService.showErrorToast(
            'Failed to updated Additional Field',
          );
          throw err;
        },
      );
  }

  deleteAdditionalField(additionalField: AdditionalField) {
    this.deletingAdditionalField = true;
    this.deleteAdditionalFieldGql
      .mutate({
        id: additionalField.id,
      })
      .subscribe(
        (res) => {
          this.deletingAdditionalField = false;
          this.company.additionalFields.splice(
            this.company.additionalFields.findIndex(
              (af) => af.id === res.data.deleteAdditionalField.id,
            ),
            1,
          );
          this.toastService.showToast('Additional Field deleted');
        },
        (err) => {
          this.deletingAdditionalField = false;
          this.toastService.showErrorToast('Failed to delete Additional Field');
          throw err;
        },
      );
  }

  show = () => this.modalBase.show();
  hide = () => this.modalBase.hide();
  setLoading = (isLoading: boolean) => this.modalBase.setLoading(isLoading);
}
